import React from "react";
import { Box, Button, Stack, Typography, alpha } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import TrackerToggle from "./TrackerToggle";

const TrackerManualEntryState = ({
  onOpenDeduction,
  onEnableTracker,
  canEdit = true,
  loading = false,
  transactionCount = 0,
}) => (
  <Box
    sx={{
      mx: { xs: 1, sm: 2 },
      overflow: "hidden",
      border: "1px solid",
      borderColor: (theme) => alpha(theme.palette.error.main, 0.4),
      borderRadius: 2,
      bgcolor: "background.paper",
    }}
  >
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={2.5}
      alignItems='flex-start'
      sx={{
        p: { xs: 2.5, sm: 4 },
        bgcolor: (theme) =>
          alpha(
            theme.palette.error.main,
            theme.palette.mode === "dark" ? 0.1 : 0.035
          ),
      }}
    >
      <Box
        sx={{
          display: "grid",
          placeItems: "center",
          width: 56,
          height: 56,
          flexShrink: 0,
          borderRadius: 2,
          color: "error.main",
          bgcolor: (theme) => alpha(theme.palette.error.main, 0.12),
        }}
      >
        <PowerSettingsNewIcon aria-hidden='true' sx={{ fontSize: 32 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant='overline'
          color='text.secondary'
          sx={{ fontWeight: 600, letterSpacing: "0.08em", lineHeight: 1.5 }}
        >
          Manual entry
        </Typography>
        <Typography
          component='h3'
          variant='h6'
          sx={{
            mt: 0.5,
            fontWeight: 700,
            color: (theme) =>
              theme.palette.mode === "dark"
                ? theme.palette.error.light
                : theme.palette.error.dark,
          }}
        >
          Tracker is OFF
        </Typography>
        <Typography
          variant='body2'
          color='text.secondary'
          sx={{ mt: 1, maxWidth: 560, lineHeight: 1.7 }}
        >
          With the tracker off, enter share costs in Column I of the Qualifying
          Deduction Schedule. You can continue preparing your deduction there.
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          useFlexGap
          flexWrap='wrap'
          gap={1.5}
          sx={{ mt: 2.5 }}
        >
          {onEnableTracker && (
            <TrackerToggle
              checked={false}
              disabled={!canEdit}
              loading={loading}
              onChange={(checked) => {
                if (checked) onEnableTracker();
              }}
            />
          )}
          {onOpenDeduction && (
            <Button
              variant='contained'
              disableElevation
              endIcon={<ArrowForwardIcon />}
              onClick={onOpenDeduction}
              disabled={loading}
              sx={{
                textTransform: "none",
                textAlign: "left",
                maxWidth: "100%",
                backgroundColor: "secondary.main",
                "&:hover": {
                  backgroundColor: "secondary.dark",
                },
              }}
            >
              Open Qualifying Deduction Schedule
            </Button>
          )}
        </Stack>
      </Box>
    </Stack>
    <Stack
      direction='row'
      spacing={1}
      alignItems='flex-start'
      sx={{
        px: { xs: 2.5, sm: 4 },
        py: 1.5,
        borderTop: "1px solid",
        borderColor: "divider",
        bgcolor: (theme) => alpha(theme.palette.text.primary, 0.025),
        color: "text.secondary",
      }}
    >
      <Inventory2OutlinedIcon
        aria-hidden='true'
        sx={{ fontSize: 18, mt: 0.25, flexShrink: 0 }}
      />
      <Typography variant='body2' sx={{ lineHeight: 1.6 }}>
        {transactionCount > 0
          ? `${transactionCount} tracker ${transactionCount === 1 ? "entry is" : "entries are"} retained. Turn on “Use tracker” to continue working with them.`
          : "Turn on “Use tracker” whenever you want to calculate share costs from acquisitions and transfers."}
      </Typography>
    </Stack>
  </Box>
);

export default TrackerManualEntryState;
